import axios from "axios";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import dayjs from "dayjs";
import { AiFillWarning } from "react-icons/ai";
import relativeTime from "dayjs/plugin/relativeTime";
import { getIdToken, requireIdToken } from "../../../utils/firebaseAuth";
import toast from "react-hot-toast";
import useAuthModal from "../../../hooks/useAuthModal";
import VerifiedAccount from "../../../components/VerifiedAccount";
import Link from "next/link";
import { Spinner } from "@jammable/ui-components/Loader";
import NotFound from "../../../components/NotFound";
import { auth } from "../../../utils/firebaseAdmin";
import { useModal } from "../../../context/ModalContext";
import CreateCollectionModal from "../../../components/collection/modals/CreateCollectionModal";
import Dropdown from "../../../components/dropdown/Dropdown";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import { IoFlame, IoPeopleCircle } from "react-icons/io5";
import { FaStar, FaStopwatch } from "react-icons/fa";
import { ChevronDown } from "lucide-react";
import { SocialIcon } from "react-social-icons";
import SendMessageModal from "../../../components/modals/SendMessageModal";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import millify from "millify";
import BlockUserModal from "../../../components/modals/BlockUserModal";
import { FaUserShield } from "react-icons/fa";
import { BsThreeDots } from "react-icons/bs";
import ReportModal from "../../../components/modals/ReportModal";
import connectToDb from "../../../utils/mongodb";
import {
  JamVoiceCard,
  JamCollectionCard,
  JamCardSkeleton,
  JamVoiceCardSkeleton,
} from "../../../components/home/JamCards";

const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const Stat = ({ value, label }) => (
  <span className="tw-flex tw-flex-col tw-items-center md:tw-flex-row md:tw-gap-1">
    {value}
    <span className="tw-font-bold tw-text-jam-muted">{label}</span>
  </span>
);

const UserPage = ({ userData }) => {
  dayjs.extend(relativeTime);

  const [user, setUser] = useState(userData?.user);

  // PROFILE_VOICES_PAGE_SIZE in utils/profile (server-only, so not imported).
  const limit = 20;
  const [followers, setFollowers] = useState(userData?.user?.followers ?? 0);
  const [followLoading, setFollowLoading] = useState(false);
  const { checkAuthAndExecute } = useAuthModal();
  const { openModal, closeModal } = useModal();
  const [sortBy, setSortBy] = useState("hot");
  const [tab, setTab] = useState("voices");

  const [isFollowing, setIsFollowing] = useState(
    userData?.user?.isFollowing ?? false,
  );

  // The profile's owner is named by the Mongo user _id; the page no longer
  // has their Firebase uid (see getServerSideProps).
  const profileId = userData?.user?._id;
  const voicesPage = userData?.voicesPage ?? 1;
  const voicePages = userData?.voicePages ?? 0;

  const fetchUserCollections = async () => {
    try {
      const token = await getIdToken();
      const response = await axios.get("/api/collection/getUserCollections", {
        params: {
          profile: profileId,
        },
        headers: {
          Authorization: token,
        },
      });
      return response.data;
    } catch (error) {
      console.log("Failed to fetch collections");
      throw new Error("Failed to fetch collections");
    }
  };

  const { data, isLoading } = useQuery(
    ["userCollections", profileId],
    fetchUserCollections,
  );

  const fetchPosts = async ({ pageParam = 0 }) => {
    const token = await getIdToken();

    const res = await axios.get(`/api/user/getUserPosts?page=${pageParam}`, {
      params: {
        limit: limit,
        profile: profileId,
        sortBy: sortBy,
      },
      headers: {
        Authorization: token,
      },
    });
    const data = res.data;
    return data;
  };

  const posts = useInfiniteQuery(
    ["user-posts", profileId, sortBy, voicesPage],
    fetchPosts,
    {
      // The server rendered this page of voices into the HTML (visitors only;
      // owners also see private voices and fetch here).
      initialData:
        sortBy === "hot" && userData?.initialPosts
          ? { pages: [userData.initialPosts], pageParams: [voicesPage] }
          : undefined,
      staleTime: 60 * 1000,
      getNextPageParam: (lastpage, allpages) => {
        const maxPages = Math.ceil(lastpage?.length / limit);
        // A server-rendered ?page=N starts the list at N, not 1.
        const nextPage = (lastpage?.page ?? allpages.length) + 1;
        return nextPage <= maxPages ? nextPage : null;
      },
    },
  );

  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    const handleScroll = async (e) => {
      if (
        tab === "voices" &&
        !fetching &&
        window.innerHeight + e.target.documentElement.scrollTop + 800 >=
          e.target.documentElement.scrollHeight &&
        !posts.isFetchingNextPage
      ) {
        setFetching(true);
        if (posts.hasNextPage) {
          await posts.fetchNextPage();
        }
        setFetching(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [posts, fetching, tab]);

  useEffect(() => {
    setUser(userData?.user);
  }, [userData]);

  const follow = async () => {
    if (!checkAuthAndExecute()) return false;

    setFollowLoading(true);
    const token = await requireIdToken();
    if (isFollowing) {
      setFollowers(followers - 1);
      setIsFollowing(false);
    }
    axios
      .post(
        `/api/user/followUser`,
        { profile: profileId },
        {
          headers: {
            Authorization: token,
          },
        },
      )
      .then((res) => {
        const r = res.data.isFollowing;
        if (r === 0) {
          setIsFollowing(!isFollowing);
          setFollowers(followers + 1);
          toast.success(`Successfully followed!`);
        } else {
          setIsFollowing(!isFollowing);
          setFollowers(followers - 1);
          toast.success(`Successfully unfollowed!`);
        }
      })
      .finally(() => {
        setFollowLoading(false);
      });
  };

  const message = async () => {
    const token = await requireIdToken();
    const res = await axios.get(`/api/message/checkConversation`, {
      params: {
        profile: profileId,
      },
      headers: {
        Authorization: token,
      },
    });
    if (res.data.exists) {
      router.push(`/messages/${res.data.conversationId}`);
    } else {
      openModal(
        "send-message-modal",
        <SendMessageModal
          receiverProfile={profileId}
          closeModal={() => closeModal("send-message-modal")}
        />,
      );
    }
  };

  const router = useRouter();

  const title =
    voicesPage > 1
      ? `${user?.username} - AI Voices, Page ${voicesPage} | Jammable`
      : `${user?.username} - AI Voice Covers & Creations | Jammable`;
  const description = `@${user?.username} ${followers} ${
    followers === 1 ? "Follower" : "Followers"
  } - Check out ${user?.username} on Jammable!`;
  const url = `https://www.jammable.com/@${user?.username}`;
  const voicesPageHref = (page) =>
    page > 1
      ? `/@${encodeURIComponent(user?.username)}?page=${page}`
      : `/@${encodeURIComponent(user?.username)}`;
  // Each ?page=N lists different voices, so it is its own canonical.
  const canonical =
    voicesPage > 1
      ? `https://www.jammable.com${voicesPageHref(voicesPage)}`
      : url;

  const share = () => {
    navigator.clipboard.writeText(url);
    toast.success("Profile link copied!");
  };

  // Most of the ~8.9M accounts have empty profiles. Google crawls these thin
  // pages and rejects them, inflating the sitewide "Crawled – currently not
  // indexed" pile and draining crawl budget. noindex a profile until it lists
  // a public voice; it becomes indexable again automatically once it does.
  // Followers used to count too, but a follower is not content: 24 of 32
  // sampled sitemap profiles (2026-10-10) had followers and no voices or
  // covers - a username and a footer.
  const noindexEmptyProfile = (userData?.modelCount ?? 0) === 0;

  const isBlocked = userData?.isBlockedByUser || userData?.hasBlockedUser;
  const voices = (posts?.data?.pages || []).flatMap(
    (page) => page?.posts || [],
  );
  const collections = data?.collections || [];

  const pillButton =
    "tw-flex tw-h-[50px] tw-flex-grow tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-px-7 tw-text-[15px] tw-font-extrabold tw-no-underline md:tw-flex-grow-0";

  return (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        noindex={noindexEmptyProfile}
        openGraph={{
          title: title,
          description: description,
          images: [
            {
              url: user?.avatar,
              width: 900,
              height: 900,
              alt: "Jammable Model",
            },
          ],
        }}
      />
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-6 tw-px-4 tw-pb-28 tw-pt-2 md:tw-px-8">
        <section className="tw-flex tw-flex-col tw-items-center tw-gap-5 tw-rounded-[34px] tw-bg-jam-lav tw-p-6 tw-text-center md:tw-flex-row md:tw-items-center md:tw-gap-6 md:tw-rounded-[44px] md:tw-p-8 md:tw-text-left">
          <div className="tw-relative tw-h-[110px] tw-w-[110px] tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full tw-border-[5px] tw-border-solid tw-border-white md:tw-h-[140px] md:tw-w-[140px] md:tw-border-[6px]">
            <Image
              fill
              sizes="140px"
              priority
              quality={75}
              alt={`${user?.username} profile picture`}
              referrerPolicy="no-referrer"
              draggable="false"
              src={user?.avatar}
              className="tw-object-cover"
            />
          </div>

          <div className="tw-flex tw-min-w-0 tw-flex-grow tw-flex-col tw-items-center tw-gap-2.5 md:tw-items-start">
            <h1 className="jam-display tw-m-0 tw-flex tw-max-w-full tw-items-center tw-gap-2 tw-break-all tw-text-[32px] tw-font-extrabold tw-leading-none md:tw-text-[46px]">
              {user?.username}
              <VerifiedAccount verified={user?.verifiedAccount} size={26} />
            </h1>
            <div className="tw-text-[13px] tw-font-bold tw-text-jam-muted md:tw-text-[15px]">
              @{user?.username}
              {user?.createdAt
                ? ` · joined ${dayjs(user.createdAt).format("MMM YYYY")}`
                : ""}
            </div>
            {user?.description && (
              <div className="tw-max-w-[560px] tw-text-[15px] tw-font-semibold">
                {user.description}
              </div>
            )}
            <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-x-5 tw-gap-y-2 tw-text-[13px] tw-font-extrabold md:tw-justify-start md:tw-text-sm">
              <Stat
                value={millify(followers || 0)}
                label={followers === 1 ? "follower" : "followers"}
              />
              {typeof user?.following === "number" && (
                <Stat value={millify(user.following)} label="following" />
              )}
              <Stat
                value={millify(userData?.modelCount ?? 0)}
                label={userData?.modelCount === 1 ? "voice" : "voices"}
              />
              <Stat
                value={millify(user?.stats?.covers ?? 0)}
                label={user?.stats?.covers === 1 ? "cover" : "covers"}
              />
            </div>
            {(userData?.audienceCount > 0 ||
              userData?.starCount > 0 ||
              user?.socialLinks?.length > 0) && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 md:tw-justify-start">
                {userData?.audienceCount > 0 && (
                  <span className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold">
                    <IoPeopleCircle size={16} className="tw-text-jam-purple" />
                    {millify(userData.audienceCount)} audience · last 30 days
                    <span className="tw-h-1.5 tw-w-1.5 tw-animate-pulse tw-rounded-full tw-bg-green-500" />
                  </span>
                )}
                {userData?.starCount > 0 && (
                  <span className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-lime tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold">
                    <FaStar size={12} />
                    {userData.starCount}{" "}
                    {userData.starCount === 1
                      ? "Community Favorite"
                      : "Community Favorites"}
                  </span>
                )}
                {user?.socialLinks?.map((link) =>
                  isValidUrl(link) ? (
                    <SocialIcon
                      style={{
                        height: 32,
                        width: 32,
                        maxWidth: 32,
                        maxHeight: 32,
                      }}
                      key={link}
                      url={link}
                      target="_blank"
                    />
                  ) : null,
                )}
              </div>
            )}
          </div>

          <div className="tw-flex tw-w-full tw-items-center tw-gap-2 md:tw-w-auto md:tw-flex-col md:tw-items-stretch md:tw-self-start">
            {userData?.isSameUser ? (
              <>
                <Link
                  href={`/settings`}
                  className={`${pillButton} tw-bg-jam-purple tw-text-white`}
                >
                  Edit profile
                </Link>
                <button
                  type="button"
                  onClick={share}
                  className={`${pillButton} tw-bg-white tw-text-jam-ink`}
                >
                  Share
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={followLoading}
                  onClick={follow}
                  className={`${pillButton} ${
                    isFollowing
                      ? "tw-bg-white tw-text-jam-ink"
                      : "tw-bg-jam-purple tw-text-white"
                  } disabled:tw-opacity-70`}
                >
                  {followLoading ? (
                    <Spinner className="tw-border-3" size={4} />
                  ) : isFollowing ? (
                    "Unfollow"
                  ) : (
                    "Follow"
                  )}
                </button>
                <button
                  type="button"
                  onClick={message}
                  className={`${pillButton} tw-bg-white tw-text-jam-ink`}
                >
                  Message
                </button>
                <Dropdown
                  className="tw-p-1"
                  dropdownItems={
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          if (!checkAuthAndExecute()) return false;
                          openModal(
                            "report-modal",
                            <ReportModal
                              type="user"
                              item={user}
                              closeModal={() => closeModal("report-modal")}
                            />,
                          );
                        }}
                      >
                        <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-3xl tw-p-2 tw-font-extrabold tw-transition-all hover:tw-bg-jam-lav">
                          <AiFillWarning size={18} />
                          Report User
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          openModal(
                            "block-user-modal",
                            <BlockUserModal
                              user={user}
                              isBlocked={userData?.hasBlockedUser}
                              redirectUrl={`/@${user?.username}`}
                            />,
                          );
                        }}
                      >
                        <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-3xl tw-p-2 tw-font-extrabold tw-transition-all hover:tw-bg-jam-lav">
                          <FaUserShield size={20} />
                          {userData?.hasBlockedUser
                            ? "Unblock User"
                            : "Block User"}
                        </div>
                      </DropdownMenuItem>
                    </>
                  }
                >
                  <div
                    aria-label="More"
                    className="tw-flex tw-h-[50px] tw-w-[50px] tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-jam-ink md:tw-w-full"
                  >
                    <BsThreeDots size={22} />
                  </div>
                </Dropdown>
              </>
            )}
          </div>
        </section>

        {userData?.isBlockedByUser ? (
          <NotFound
            title="You are blocked by this user"
            description="You cannot view this user's profile."
            animateIn={false}
          />
        ) : userData?.hasBlockedUser ? (
          <NotFound
            title="You have blocked this user"
            description="You cannot view this user's profile."
            animateIn={false}
          />
        ) : null}

        {!isBlocked && (
          <>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
              <div className="tw-flex tw-w-full tw-gap-1 tw-rounded-full tw-bg-jam-lav tw-p-1 tw-text-[13px] tw-font-extrabold sm:tw-w-auto md:tw-p-[5px] md:tw-text-[15px]">
                {[
                  ["voices", "Voices", userData?.modelCount],
                  ["collections", "Collections", userData?.collectionsCount],
                ].map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={tab === key}
                    onClick={() => setTab(key)}
                    className={`tw-flex-grow tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-5 tw-py-2.5 ${
                      tab === key
                        ? "tw-bg-white tw-text-jam-purple"
                        : "tw-bg-transparent tw-text-jam-ink"
                    }`}
                  >
                    {label}
                    <span className="tw-ml-1.5 tw-text-jam-muted">
                      {millify(count ?? 0)}
                    </span>
                  </button>
                ))}
              </div>

              {tab === "voices" ? (
                <Dropdown
                  className="tw-p-1"
                  dropdownItems={
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          setSortBy("hot");
                        }}
                      >
                        <div
                          className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-3xl tw-p-2 tw-font-extrabold tw-transition-all hover:tw-bg-jam-lav ${sortBy === "hot" && "tw-bg-jam-lav tw-text-jam-purple"}`}
                        >
                          <IoFlame size={20} />
                          Hot Voices
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSortBy("new");
                        }}
                      >
                        <div
                          className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-3xl tw-p-2 tw-font-extrabold tw-transition-all hover:tw-bg-jam-lav ${sortBy === "new" && "tw-bg-jam-lav tw-text-jam-purple"}`}
                        >
                          <FaStopwatch size={18} />
                          New Voices
                        </div>
                      </DropdownMenuItem>
                    </>
                  }
                >
                  <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-lav tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold">
                    {sortBy === "hot" ? "Most used" : "Newest"}
                    <ChevronDown size={16} strokeWidth={2.6} />
                  </div>
                </Dropdown>
              ) : userData?.isSameUser ? (
                <button
                  type="button"
                  onClick={() => {
                    openModal(
                      "create-collection-modal",
                      <CreateCollectionModal />,
                    );
                  }}
                  className="tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-purple tw-px-5 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-white"
                >
                  Create collection
                </button>
              ) : null}
            </div>

            {tab === "voices" && (
              <>
                {posts?.data?.pages[0]?.posts.length === 0 ? (
                  <NotFound
                    title="No voices found..."
                    description={`${user?.username} has no public voices :(`}
                    animateIn={false}
                  />
                ) : null}
                <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 md:tw-grid-cols-6 md:tw-gap-3.5">
                  {posts.isLoading
                    ? [...Array(12)].map((_, i) => (
                        <JamVoiceCardSkeleton key={i} />
                      ))
                    : voices.map((model) => (
                        <JamVoiceCard key={model._id} model={model} />
                      ))}
                </div>
                {posts?.isFetchingNextPage && (
                  <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
                    <Spinner size={5} className="tw-border-3" />
                  </div>
                )}
                {/* Plain links so crawlers reach every public voice, not just
                    the first page; people mostly get the infinite scroll, and
                    this goes once it has loaded more. */}
                {userData?.initialPosts &&
                  sortBy === "hot" &&
                  voicePages > 1 &&
                  (posts?.data?.pages?.length ?? 0) <= 1 && (
                    <nav
                      aria-label="More voices"
                      className="tw-flex tw-items-center tw-justify-center tw-gap-5 tw-py-6 tw-text-[13px] tw-font-extrabold md:tw-text-sm"
                    >
                      {voicesPage > 1 && (
                        <Link
                          href={voicesPageHref(voicesPage - 1)}
                          className="tw-text-jam-purple tw-no-underline"
                        >
                          ← Previous
                        </Link>
                      )}
                      <span className="tw-text-jam-muted">
                        Page {voicesPage} of {voicePages}
                      </span>
                      {voicesPage < voicePages && (
                        <Link
                          href={voicesPageHref(voicesPage + 1)}
                          className="tw-text-jam-purple tw-no-underline"
                        >
                          More voices →
                        </Link>
                      )}
                    </nav>
                  )}
              </>
            )}

            {tab === "collections" && (
              <>
                {!isLoading && collections.length === 0 ? (
                  <NotFound
                    title="No collections found..."
                    description={`${user?.username} has no collections :(`}
                    animateIn={false}
                  />
                ) : null}
                <div className="tw-grid tw-grid-cols-2 tw-gap-3.5 md:tw-grid-cols-5">
                  {isLoading
                    ? [...Array(5)].map((_, i) => (
                        <JamCardSkeleton key={i} className="tw-h-[210px]" />
                      ))
                    : collections.map((collection) => (
                        <JamCollectionCard
                          key={collection._id}
                          collection={collection}
                        />
                      ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserPage;

export async function getServerSideProps(context) {
  const User = (await import("../../../models/User")).default;
  const Model = (await import("../../../models/Model")).default;
  const Collection = (await import("../../../models/Collection")).default;
  const { MODEL_PROJECTION } = await import("../../api/model");
  const { PROFILE_VOICES_PAGE_SIZE, profileVoicesMatch, profileVoicesSort } =
    await import("../../../utils/profile");
  try {
    const { id } = context.query;
    const token = context?.req?.cookies?.token;
    await connectToDb();

    let firebaseUid = null;
    if (token) {
      try {
        const decodedToken = await auth.verifyIdToken(token);
        firebaseUid = decodedToken?.uid;
      } catch (error) {
        console.log("no token");
      }
    }

    const user = await User.aggregate([
      {
        $match: {
          $or: [{ firebaseUid: id }, { username: id }],
        },
      },
      {
        $addFields: {
          isFollowing: {
            $cond: {
              if: {
                $and: [
                  { $eq: [{ $type: "$followedByIds" }, "array"] },
                  { $in: [firebaseUid, "$followedByIds"] },
                ],
              },
              then: true,
              else: false,
            },
          },
        },
      },
      {
        $project: {
          username: 1,
          avatar: 1,
          createdAt: 1,
          // Server-only, removed before the props (see publicUser below).
          firebaseUid: 1,
          followers: 1,
          following: 1,
          isFollowing: 1,
          description: 1,
          verifiedAccount: 1,
          stats: 1,
          socialLinks: 1,
          // Server-only: decides isBlockedByUser below.
          blockedUsers: 1,
        },
      },
    ]);

    if (user?.length === 0) {
      return {
        notFound: true,
      };
    }

    // These props are in the HTML of a public page. The owner's uid and
    // blockedUsers (other people's uids) used to ship to every visitor; the
    // browser now gets booleans (isSameUser, isFollowing, isBlockedByUser,
    // hasBlockedUser) and names the owner by the Mongo _id, which the
    // profile's APIs accept as `profile`. The avatar URL still contains the
    // uid - that needs a file migration.
    const {
      firebaseUid: ownerUid,
      blockedUsers: ownerBlockedUsers,
      ...publicUser
    } = user[0];

    const [userCount, currentUser] = await Promise.all([
      Model.aggregate([
        {
          $match: {
            accessIds: ownerUid,
          },
        },
        {
          $group: {
            _id: null,
            totalUniqueUsers: { $sum: "$stats.uniqueUsers.30d.count" },
            modelCount: { $sum: 1 },
          },
        },
      ]),
      firebaseUid
        ? User.findOne({ firebaseUid }, { blockedUsers: 1 }).lean()
        : null,
    ]);

    const [models, collections, starCount] = await Promise.all([
      Model.count(profileVoicesMatch(ownerUid, false)),
      Collection.count({
        userId: ownerUid,
        deleted: { $ne: true },
        isPrivate: { $ne: true },
      }),
      Model.count({
        isCommunityFavorite: true,
        accessIds: ownerUid,
      }),
    ]);

    const isSameUser = firebaseUid === ownerUid;
    const isBlockedByUser = Boolean(
      firebaseUid && ownerBlockedUsers?.includes(firebaseUid),
    );
    const hasBlockedUser = Boolean(
      currentUser?.blockedUsers?.includes(ownerUid),
    );

    const audienceCount = userCount[0]?.totalUniqueUsers;

    // The creator's public voices go into the HTML as plain links, so Google
    // can reach voice pages from the profile (it used to get none: the list
    // only arrived by client-side fetch). Same query and default "hot" sort
    // as the visitor's /api/user/getUserPosts call, and react-query is seeded
    // with it. 417 of 13,546 creators (2026-10-11) have more than one page of
    // public voices - 35,315 voices past page 1, 49% of all public ones; the
    // largest has 4,592 - so the rest are reached through ?page=N, each page
    // server-rendered and linked from the one before. Owners also see their
    // private voices, so they keep the client fetch from the first page.
    const voicePages = Math.ceil(models / PROFILE_VOICES_PAGE_SIZE);
    const pageParam = context.query.page;
    let voicesPage = 1;
    if (pageParam !== undefined && !isSameUser) {
      voicesPage = /^\d+$/.test(String(pageParam)) ? Number(pageParam) : NaN;
      if (!(voicesPage >= 1 && voicesPage <= Math.max(voicePages, 1))) {
        return { notFound: true };
      }
    }

    let initialPosts = null;
    if (!isSameUser && !isBlockedByUser && !hasBlockedUser && models > 0) {
      const posts = await Model.aggregate([
        { $match: profileVoicesMatch(ownerUid, false) },
        { $sort: profileVoicesSort("hot") },
        { $skip: (voicesPage - 1) * PROFILE_VOICES_PAGE_SIZE },
        { $limit: PROFILE_VOICES_PAGE_SIZE },
        { $project: MODEL_PROJECTION },
      ]);
      initialPosts = { posts, length: models, page: voicesPage };
    }

    console.log(userCount);
    return {
      props: {
        userData: JSON.parse(
          JSON.stringify({
            user: publicUser,
            isSameUser,
            modelCount: models,
            starCount: starCount,
            audienceCount: audienceCount,
            collectionsCount: collections,
            isBlockedByUser,
            hasBlockedUser,
            initialPosts,
            voicesPage,
            voicePages,
          }),
        ),
      },
    };
  } catch (error) {
    console.log(error);

    return {
      notFound: true,
    };
  }
}
